/**
 * This browser's half of the account's "Stay signed in" choice
 * (`SESSION_LIFETIMES` in `packages/domain`): when a page of the app that
 * starts sign-in (every `/app` page, `/sign-in`, `/sign-up`) was last opened
 * or brought back into view here, or last called the api from a tab in view,
 * and which choice the account last told this browser about.
 *
 * Both live in `localStorage`, next to the session they time. The choice is
 * the account's and is stored on it; the copy here exists because the check
 * that ends a session runs before the app has asked the api anything
 * (`sessionHandle.ts`). It is refreshed whenever the account is read
 * (`SessionLifetimeSync`) or saved (the account screen), so a choice made on
 * another device applies here from the visit after the one that learnt it.
 * Until this browser has learnt it, {@link DEFAULT_SESSION_LIFETIME} applies.
 *
 * A third value is this browser's alone: whether "Keep me signed in on this
 * device" was unchecked at its last sign-in (#1120). Unchecked, the browser
 * applies {@link SHARED_DEVICE_SESSION_LIFETIME} instead of the account's
 * choice ({@link appliedSessionLifetime}).
 *
 * Storage that refuses costs only the timing: no visit read means no idle
 * limit is applied, and no remembered choice means the default.
 */
import {
  DEFAULT_SESSION_LIFETIME,
  SHARED_DEVICE_SESSION_LIFETIME,
  sessionLifetimeIdleMs,
  sessionLifetimeSchema,
  type SessionLifetime,
} from "@reflect/domain";

import { webStorage } from "../browserStorage";

/** `localStorage` key holding the last visit, as epoch milliseconds. */
export const LAST_VISIT_STORAGE_KEY = "reflect.lastVisitAt";

/** `localStorage` key holding the account's choice, as its wire code. */
export const SESSION_LIFETIME_STORAGE_KEY = "reflect.sessionLifetime";

/**
 * `localStorage` key that marks this browser as signed in with "Keep me
 * signed in on this device" unchecked. Absent means checked, the default.
 */
export const KEEP_SIGNED_IN_STORAGE_KEY = "reflect.keepSignedIn";

/** The value {@link KEEP_SIGNED_IN_STORAGE_KEY} holds for an unchecked box. */
const NOT_KEPT = "0";

/**
 * When a page of the app that starts sign-in was last opened, or brought back
 * into view, or called the api from a tab in view, in this browser. Signed in
 * or not: the sign-in page records one too, so a sign-in made there is not
 * ended by a visit from before it.
 *
 * @returns epoch milliseconds, or `null` when none is recorded or readable.
 */
export function readLastVisit(): number | null {
  try {
    const raw = webStorage("local")?.getItem(LAST_VISIT_STORAGE_KEY) ?? null;
    if (raw === null) return null;
    const at = Number(raw);
    return Number.isFinite(at) ? at : null;
  } catch {
    return null;
  }
}

/**
 * Record a visit.
 *
 * @param now - the visit's time, epoch milliseconds.
 */
export function recordVisit(now: number): void {
  try {
    webStorage("local")?.setItem(LAST_VISIT_STORAGE_KEY, String(now));
  } catch {
    // Quota or a refusing store. The old visit must not stay behind: every
    // check would find it, and end each new sign-in as it lands. With none,
    // the next check applies no idle limit, keeping the session rather than
    // ending it early. Removing frees space, so it works on a full store.
    try {
      webStorage("local")?.removeItem(LAST_VISIT_STORAGE_KEY);
    } catch {
      // A store that refuses reads too: the next check reads no visit anyway.
    }
  }
}

/**
 * The account's choice as this browser last learnt it.
 *
 * @returns the remembered choice, or the default when none is remembered.
 */
export function rememberedSessionLifetime(): SessionLifetime {
  try {
    const parsed = sessionLifetimeSchema.safeParse(
      webStorage("local")?.getItem(SESSION_LIFETIME_STORAGE_KEY),
    );
    return parsed.success ? parsed.data : DEFAULT_SESSION_LIFETIME;
  } catch {
    return DEFAULT_SESSION_LIFETIME;
  }
}

/**
 * Remember the account's choice for the next check in this browser.
 *
 * @param lifetime - the choice the api returned.
 */
export function rememberSessionLifetime(lifetime: SessionLifetime): void {
  try {
    webStorage("local")?.setItem(SESSION_LIFETIME_STORAGE_KEY, lifetime);
  } catch {
    // Not remembered: the next check applies the default instead.
  }
}

/**
 * Remember the "Keep me signed in on this device" box as a sign-in starts
 * here.
 *
 * Written as the attempt starts, before any provider is asked: a popup must
 * open with no `await` in front of it, and an emailed link completes in
 * another tab, which reads it from here. An attempt that fails leaves it
 * behind until the next one — sign-in writes the box, sign-up clears it — and
 * it can only shorten a sign-in. Storage that refuses the write leaves the
 * account's choice in force.
 *
 * @param keep - whether the box was checked.
 */
export function rememberKeepSignedIn(keep: boolean): void {
  try {
    const store = webStorage("local");
    if (keep) store?.removeItem(KEEP_SIGNED_IN_STORAGE_KEY);
    else store?.setItem(KEEP_SIGNED_IN_STORAGE_KEY, NOT_KEPT);
  } catch {
    // Unwritable storage leaves the account's choice in force.
  }
}

/**
 * Whether this browser's last sign-in kept the box checked.
 *
 * @returns `false` only when it was unchecked; `true` when nothing, or
 *   nothing readable, is stored.
 */
export function keepsSignedIn(): boolean {
  try {
    return webStorage("local")?.getItem(KEEP_SIGNED_IN_STORAGE_KEY) !== NOT_KEPT;
  } catch {
    return true;
  }
}

/**
 * The choice the check applies in this browser: the account's, or, where the
 * box was unchecked, {@link SHARED_DEVICE_SESSION_LIFETIME}, which is never
 * the longer of the two.
 *
 * @returns the choice in force here.
 */
export function appliedSessionLifetime(): SessionLifetime {
  return keepsSignedIn() ? rememberedSessionLifetime() : SHARED_DEVICE_SESSION_LIFETIME;
}

/** What the check knows about a session this browser may be storing. */
export interface StoredSessionState {
  /** The choice in force in this browser ({@link appliedSessionLifetime}). */
  lifetime: SessionLifetime;
  /** The last visit, or `null` when none is recorded. */
  lastVisitAt: number | null;
  /** The time now, epoch milliseconds. */
  now: number;
}

/**
 * Whether a session stored in this state has outlived the choice: its last
 * visit is more than that many days ago. No recorded visit ends nothing, so
 * the release that starts recording visits signs nobody out.
 *
 * @param state - the choice and the last visit.
 * @returns `true` when the stored session is to be signed out.
 */
export function sessionHasEnded(state: StoredSessionState): boolean {
  if (state.lastVisitAt === null) return false;
  return state.now - state.lastVisitAt > sessionLifetimeIdleMs(state.lifetime);
}
